import BigNumber from 'bignumber.js';

import { addSeparators, DEFAULT_SIGNS, toSignificant as toSignificantFunc } from './helpers';

export type BN = BigNumber;

declare module 'bignumber.js' {
  interface BigNumber {
    str: () => string;
    fromDecimals: (decimals: number) => BigNumber;
    toDecimals: (decimals: number) => BigNumber;

    sub: (n: BigNumber.Value) => BigNumber;
    add: (n: BigNumber.Value) => BigNumber;
    mul: (n: BigNumber.Value) => BigNumber;

    toSignificant: (
      decimals?: number,
      params?: { signs?: number; toFixed?: number; roundUp?: boolean }
    ) => string;
    toView: (
      decimals?: number,
      params?: { signs?: number; separator?: string; toFixed?: number }
    ) => string;
  }
}

BigNumber.prototype.str = function str(): string {
  return this.decimalPlaces(0, BigNumber.ROUND_DOWN).toFixed();
};

BigNumber.prototype.fromDecimals = function fromDecimals(decimals: number): BigNumber {
  return this.decimalPlaces(decimals, BigNumber.ROUND_DOWN).div(new BigNumber(10).pow(decimals));
};

BigNumber.prototype.toDecimals = function toDecimals(decimals: number): BigNumber {
  return this.mul(new BigNumber(10).pow(decimals)).decimalPlaces(0, BigNumber.ROUND_DOWN);
};

BigNumber.prototype.sub = function sub(n: BigNumber.Value): BigNumber {
  return this.minus(n);
};

BigNumber.prototype.add = function add(n: BigNumber.Value): BigNumber {
  return this.plus(n);
};

BigNumber.prototype.mul = function mul(n: BigNumber.Value): BigNumber {
  return this.multipliedBy(n);
};

BigNumber.prototype.toSignificant = function toSignificant(
  decimals = 0,
  params?: { signs?: number; toFixed?: number; roundUp?: boolean }
): string {
  return toSignificantFunc(this.toString(), decimals, params);
};

BigNumber.prototype.toView = function toView(
  decimals = 0,
  params?: { signs?: number; separator?: string; toFixed?: number }
): string {
  const value = toSignificantFunc(this.toString(), decimals, params);
  const bnValue = BigNumber(value);

  if (bnValue.isNaN()) return value;
  if (bnValue.isZero() && !BigNumber(this.toString()).isZero()) {
    return `${bnValue.lt(0) ? '> -' : '< '}${BigNumber(1)
      .div(10 ** (params?.signs !== undefined ? params.signs : DEFAULT_SIGNS))
      .toString()}`;
  }
  return addSeparators(value, params?.separator);
};

BigNumber.config({
  EXPONENTIAL_AT: 1e9,
  DECIMAL_PLACES: 80,
});

export { BigNumber as bn };
